import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Header from '@/components/layout/Header'
import AdminLayout from '@/components/AdminLayout'
import LoginPage from '@/pages/LoginPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import AcceptInvitationPage from '@/pages/AcceptInvitationPage'
import ForcePasswordChangePage from '@/pages/ForcePasswordChangePage'
import ProjectsPage from '@/pages/ProjectsPage'
import SuppliersPage from '@/pages/SuppliersPage'
import SupplierDetailPage from '@/pages/SupplierDetailPage'
import ProjectDetailPage from '@/pages/ProjectDetailPage'
import ReportsPage from '@/pages/ReportsPage'
import FailedAttemptsPage from '@/pages/FailedAttemptsPage'
import UsersPage from '@/pages/UsersPage'
import QualityQuestionsPage from '@/pages/QualityQuestionsPage'
import AccountSecurityPage from '@/pages/AccountSecurityPage'
import RespondentCleanupPage from '@/pages/RespondentCleanupPage'
import AdminBackfillMaskedIdsPage from '@/pages/AdminBackfillMaskedIdsPage'
import { useEffect } from 'react'

function App() {
  const { user, isLoading, refreshUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      // Remove all stylesheets for the root page
      const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
      stylesheets.forEach(sheet => sheet.remove());
    }
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18A5F9] mx-auto mb-4"></div>
          <p className="text-[#69727D]">Loading Research Clever...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={null} />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    );
  }

  // CR-17: a user flagged for a forced password reset cannot reach the app until it's done
  if (user.mustResetPassword) {
    return <ForcePasswordChangePage onComplete={refreshUser} />;
  }

  return (
    <Routes>
      <Route path="/" element={null} />
      <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin/accept-invitation" element={<AcceptInvitationPage />} />
      
      {/* Admin Routes with shared layout and Marker.io */}
      <Route path="/admin/projects" element={
        <AdminLayout>
          <ProjectsPage />
        </AdminLayout>
      } />
      <Route path="/admin/projects/:id" element={
        <AdminLayout>
          <ProjectDetailPage />
        </AdminLayout>
      } />
      <Route path="/admin/suppliers" element={
        <AdminLayout>
          <SuppliersPage />
        </AdminLayout>
      } />
      <Route path="/admin/suppliers/:id" element={
        <AdminLayout>
          <SupplierDetailPage />
        </AdminLayout>
      } />
      <Route path="/admin/reports" element={
        <AdminLayout>
          <ReportsPage />
        </AdminLayout>
      } />
      <Route path="/admin/failed-attempts" element={
        <AdminLayout>
          <FailedAttemptsPage />
        </AdminLayout>
      } />
      <Route path="/admin/users" element={
        <AdminLayout>
          <UsersPage />
        </AdminLayout>
      } />
      <Route path="/admin/quality-questions" element={
        <AdminLayout>
          <QualityQuestionsPage />
        </AdminLayout>
      } />
      <Route path="/admin/account-security" element={
        <AdminLayout>
          <AccountSecurityPage />
        </AdminLayout>
      } />
      <Route path="/admin/respondent-cleanup" element={
        <AdminLayout>
          <RespondentCleanupPage />
        </AdminLayout>
      } />
      {/* Hidden super-admin tool — not linked in the nav. */}
      <Route path="/admin/backfill-masked-ids" element={
        <AdminLayout>
          <AdminBackfillMaskedIdsPage />
        </AdminLayout>
      } />

      <Route path="*" element={<Navigate to="/admin/login" replace />} />
    </Routes>
  );
}

export default App;